import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  FilterAlt as FilterAltIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import api from '../../services/api';
import copyText from '../../utils/copyText';
import KanbanTagsNewAutoComplete from '../KanbanTagsNewAutoComplete';
import ContactAutoComplete from '../ContactAutoComplete';

export default function CreateOrEditKanbanPublicLinkModal({ open, onClose, kanbanId, initialPublicEnabled }) {
  const [publicEnabled, setPublicEnabled] = useState(!!initialPublicEnabled);
  const [loading, setLoading] = useState(false);

  const [tags, setTags] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedRequester, setSelectedRequester] = useState(null);

  const [generatingFiltered, setGeneratingFiltered] = useState(false);
  const [filteredLinkId, setFilteredLinkId] = useState('');
  const [resultOpen, setResultOpen] = useState(false);

  useEffect(() => {
    if (open) setPublicEnabled(!!initialPublicEnabled);
  }, [open, initialPublicEnabled]);

  useEffect(() => {
    if (!open || !kanbanId) return;
    let cancelled = false;
    setLoadingMeta(true);
    Promise.all([
      api.get(`/kanban/tag/${kanbanId}`).catch(() => ({ data: [] })),
      api.get(`/kanban/${kanbanId}/columns/list`).catch(() => ({ data: [] })),
    ])
      .then(([tagsRes, columnsRes]) => {
        if (cancelled) return;
        const tagList = Array.isArray(tagsRes.data) ? tagsRes.data : (tagsRes.data?.tags || []);
        const columnList = Array.isArray(columnsRes.data)
          ? columnsRes.data
          : (columnsRes.data?.columns || []);
        const orderedColumns = [...columnList]
          .sort((a, b) => {
            const posA = a?.position ?? 9999999;
            const posB = b?.position ?? 9999999;
            if (posA !== posB) return posA - posB;
            return String(a?.id || '').localeCompare(String(b?.id || ''));
          })
          .map((c) => ({
            id: c.id,
            title: c.title,
            color: c.color || undefined,
            position: c.position,
          }));
        setTags(tagList);
        setColumns(orderedColumns);
      })
      .finally(() => {
        if (!cancelled) setLoadingMeta(false);
      });
    return () => { cancelled = true; };
  }, [open, kanbanId]);

  useEffect(() => {
    if (!open) {
      setSelectedTags([]);
      setSelectedColumns([]);
      setSelectedRequester(null);
      setFilteredLinkId('');
      setResultOpen(false);
    }
  }, [open]);

  const filteredUrl = useMemo(
    () => (filteredLinkId ? `${window.location.origin}/k/${filteredLinkId}` : ''),
    [filteredLinkId]
  );

  const handleToggle = async (event) => {
    if (!kanbanId) return;
    const next = event.target.checked;
    setPublicEnabled(next);
    setLoading(true);
    try {
      await api.put(`/kanban/${kanbanId}/public`, { publicEnabled: next });
      toast.success(next ? 'Link público ativado' : 'Link público desativado');
    } catch (err) {
      console.error(err);
      setPublicEnabled(!next);
      toast.error('Não foi possível atualizar o link público');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, label = 'Link copiado') => copyText(text, label);

  const openInNewTab = (url) => {
    if (url) window.open(url, '_blank', 'noopener');
  };

  const handleGenerate = async () => {
    if (!kanbanId) return;
    if (!publicEnabled) {
      toast.error('Ative o acesso público antes de gerar o link');
      return;
    }
    setGeneratingFiltered(true);
    try {
      const { data } = await api.post(`/kanban/${kanbanId}/public-links`, {
        requesterId: selectedRequester?.id || null,
        tagIds: selectedTags.map((t) => t.id),
        columnIds: selectedColumns.map((c) => c.id),
      });
      if (data?.id) {
        setFilteredLinkId(data.id);
        setResultOpen(true);
      }
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível gerar o link filtrado');
    } finally {
      setGeneratingFiltered(false);
    }
  };

  const handleReset = () => {
    setSelectedTags([]);
    setSelectedColumns([]);
    setSelectedRequester(null);
    setFilteredLinkId('');
  };

  const onPickTags = (next) => {
    setSelectedTags(next);
    setFilteredLinkId('');
  };

  const onPickColumns = (next) => {
    setSelectedColumns(next);
    setFilteredLinkId('');
  };

  const onPickRequester = (next) => {
    setSelectedRequester(next);
    setFilteredLinkId('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PublicIcon color="primary" />
        Link público do quadro
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {publicEnabled ? <PublicIcon color="primary" /> : <LockIcon color="action" />}
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {publicEnabled ? 'Acesso público ativado' : 'Acesso público desativado'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Qualquer pessoa com o link consegue visualizar o quadro em modo somente leitura.
              </Typography>
            </Box>
          </Box>
          <Switch checked={publicEnabled} onChange={handleToggle} disabled={loading} />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          O modo público não exibe contatos, e-mails, comentários nem dados sensíveis dos
          colaboradores. Mostra apenas título, descrição, tags, prioridade, datas e progresso de
          checklists.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            opacity: publicEnabled ? 1 : 0.55,
          }}
        >
          <FilterAltIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            Filtros opcionais
          </Typography>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1.5 }}
        >
          Selecione colunas, marcadores e/ou um solicitante para gerar um link com a visão
          filtrada — ou clique em <b>Gerar link</b> sem filtros para criar o link público completo
          do quadro. Combinações idênticas reutilizam o mesmo link.
        </Typography>

        <Stack
          spacing={1.5}
          sx={{
            opacity: publicEnabled ? 1 : 0.55,
            pointerEvents: publicEnabled ? 'auto' : 'none',
          }}
        >
          <KanbanTagsNewAutoComplete
            labels={columns}
            placeholder="Colunas visíveis"
            search="Colunas do quadro"
            value={selectedColumns}
            setValue={onPickColumns}
            loading={loadingMeta}
            groupLabel="Colunas do quadro"
            globalGroupLabel="Colunas globais"
            emptyLabel="Nenhuma coluna selecionada (todas serão exibidas)"
          />

          <KanbanTagsNewAutoComplete
            labels={tags}
            placeholder="Marcadores"
            search="Marcadores do quadro"
            value={selectedTags}
            setValue={onPickTags}
            loading={loadingMeta}
            emptyLabel="Nenhum marcador selecionado"
          />

          <ContactAutoComplete
            placeholder="Solicitante"
            value={selectedRequester}
            onChange={onPickRequester}
          />

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={generatingFiltered ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerate}
              disabled={!publicEnabled || generatingFiltered}
            >
              {generatingFiltered ? 'Gerando...' : 'Gerar link'}
            </Button>
            <Button onClick={handleReset} disabled={!publicEnabled || generatingFiltered}>
              Limpar
            </Button>
          </Stack>

        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>

      <Dialog
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          Link público gerado
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Copie e envie este link. Combinações idênticas de filtros reutilizam o mesmo link, e
            os filtros ficam guardados no servidor — a URL não os revela.
          </Typography>
          <TextField
            value={filteredUrl}
            fullWidth
            size="small"
            autoFocus
            onFocus={(e) => e.target.select()}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copiar">
                    <IconButton
                      size="small"
                      onClick={() => copy(filteredUrl, 'Link filtrado copiado')}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abrir em nova aba">
                    <IconButton size="small" onClick={() => openInNewTab(filteredUrl)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              '& input': { fontFamily: 'monospace', fontSize: '0.8rem' },
            }}
          />

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">Filtros aplicados:</Typography>
            <Typography variant="body2">
              <b>Colunas:</b>{' '}
              {selectedColumns.length
                ? selectedColumns.map((c) => c.title).join(', ')
                : 'todas'}
            </Typography>
            <Typography variant="body2">
              <b>Marcadores:</b>{' '}
              {selectedTags.length
                ? selectedTags.map((t) => t.title).join(', ')
                : 'nenhum'}
            </Typography>
            <Typography variant="body2">
              <b>Solicitante:</b>{' '}
              {selectedRequester ? selectedRequester.name : 'qualquer'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => copy(filteredUrl, 'Link filtrado copiado')}
            variant="contained"
          >
            Copiar link
          </Button>
          <Button onClick={() => setResultOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
